import { useMemo } from 'react';
import { PARTIAL_SUGGESTED_ROUTE_TYPES, SUGGESTED_ROUTE_TYPES_WITH_SURGEON } from '@lp/features/RoutePlanner/config';
import { getSuggestedRouteCleanUpTimeMs } from '@lp/features/RoutePlanner/utils';
import { ServiceType } from '@/api/apis/case';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { RoutePlanType } from '@/types/route';

type UseSuggestedRoutesLoadingStateParams = {
  currentRoutePlan: RoutePlanType | null;
  currentGeneratedRouteType: RouteTypeSettingsEnum | null;
  serviceType: ServiceType;
  hasApplyPermission: boolean;
  isRoutesLoading: boolean;
  isPlansLoaded: boolean;
  isSurgeonPlansLoaded: boolean;
  isCommercialsPlansLoaded: boolean;
  routePlansCount: number;
};

type UseSuggestedRoutesLoadingStateResult = {
  isDataLoading: boolean;
  isApplyAllowed: boolean;
  isPartialRoute: boolean;
  cleanUpTimeMs: number | undefined;
};

export const useSuggestedRoutesLoadingState = ({
  currentRoutePlan,
  currentGeneratedRouteType,
  serviceType,
  hasApplyPermission,
  isRoutesLoading,
  isPlansLoaded,
  isSurgeonPlansLoaded,
  isCommercialsPlansLoaded,
  routePlansCount,
}: UseSuggestedRoutesLoadingStateParams): UseSuggestedRoutesLoadingStateResult => {
  const isPartialRoute =
    !!currentGeneratedRouteType && PARTIAL_SUGGESTED_ROUTE_TYPES.includes(currentGeneratedRouteType);

  const routeTypeWithSurgeon =
    !!currentGeneratedRouteType && SUGGESTED_ROUTE_TYPES_WITH_SURGEON.includes(currentGeneratedRouteType);
  const isSurgeonPlanRequired = routeTypeWithSurgeon && serviceType === ServiceType.NopWithTmdxSurgeon;
  const isMainDataLoaded = isSurgeonPlanRequired ? isSurgeonPlansLoaded : isPlansLoaded;
  const isDataLoaded = isMainDataLoaded && isCommercialsPlansLoaded;

  const cleanUpTimeMs = useMemo(() => {
    if (!currentRoutePlan) return undefined;

    return getSuggestedRouteCleanUpTimeMs(currentRoutePlan.segments, currentRoutePlan.legs);
  }, [currentRoutePlan]);

  return {
    isDataLoading: isRoutesLoading || (!isDataLoaded && isPlansLoaded && !!routePlansCount),
    isApplyAllowed: !!(hasApplyPermission && currentRoutePlan?.isValid && isDataLoaded),
    isPartialRoute,
    cleanUpTimeMs,
  };
};
